import { useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseQueryOptions } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ApiError, type Page, type RequestOptions } from "./api.ts";

type Key = readonly unknown[];

export function useApiQuery<T>(key: Key, path: string, opts?: RequestOptions & { enabled?: boolean; refetchInterval?: number; select?: UseQueryOptions<T>["select"] }) {
  const { enabled, refetchInterval, select, ...req } = opts ?? {};
  return useQuery<T>({ queryKey: [...key, req.query ?? null], queryFn: ({ signal }) => api.get<T>(path, { ...req, signal }), enabled, refetchInterval, select });
}

export function usePage<T>(key: Key, path: string, query: Record<string, unknown>, opts?: { enabled?: boolean; refetchInterval?: number }) {
  return useApiQuery<Page<T>>(key, path, { query: query as RequestOptions["query"], ...opts });
}

export interface MutationSpec<TVars, TData> {
  fn: (vars: TVars) => Promise<TData>;
  invalidate?: Key[] | ((data: TData, vars: TVars) => Key[]);
  success?: string | ((data: TData, vars: TVars) => string | undefined);
  error?: string;
}

export function useApiMutation<TVars = void, TData = unknown>(spec: MutationSpec<TVars, TData>, options?: UseMutationOptions<TData, ApiError, TVars>) {
  const qc = useQueryClient();
  const { onSuccess, onError, ...rest } = options ?? {};
  return useMutation<TData, ApiError, TVars>({
    ...rest,
    mutationFn: spec.fn,
    onSuccess: async (data, vars, ...ctx) => {
      const keys = typeof spec.invalidate === "function" ? spec.invalidate(data, vars) : spec.invalidate;
      if (keys) await Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k })));
      const msg = typeof spec.success === "function" ? spec.success(data, vars) : spec.success;
      if (msg) toast.success(msg);
      await (onSuccess as ((...a: unknown[]) => unknown) | undefined)?.(data, vars, ...ctx);
    },
    onError: (err, vars, ...ctx) => {
      toast.error(spec.error ?? err.message);
      (onError as ((...a: unknown[]) => unknown) | undefined)?.(err, vars, ...ctx);
    },
  });
}

export const keys = {
  operator: ["operator"] as const,
  me: ["me"] as const,
  dashboard: ["dashboard"] as const,
  companies: ["companies"] as const,
  company: (id: number | string) => ["companies", String(id)] as const,
  users: ["users"] as const,
  sftp: ["sftp-accounts"] as const,
  wmsSystems: ["wms", "systems"] as const,
  wmsLinks: ["wms", "links"] as const,
  tmsConnections: ["tms", "connections"] as const,
  transport: (shipmentId: number | string) => ["shipments", String(shipmentId), "transport"] as const,
  settings: ["settings"] as const,
  audit: ["audit"] as const,
  documents: ["documents"] as const,
  document: (id: number | string) => ["documents", String(id)] as const,
  documentTypes: ["document-types"] as const,
  partners: ["partners"] as const,
  partner: (id: number | string) => ["partners", String(id)] as const,
  catalogue: ["catalogue"] as const,
  maps: ["maps"] as const,
  map: (id: number | string) => ["maps", String(id)] as const,
  routes: ["routes"] as const,
  products: ["master-data", "products"] as const,
  locations: ["master-data", "locations"] as const,
  numberRanges: ["master-data", "number-ranges"] as const,
  priceLists: ["master-data", "price-lists"] as const,
  bookings: ["master-data", "bookings"] as const,
  pods: ["master-data", "pods"] as const,
  nonCompliance: ["master-data", "non-compliance"] as const,
  shipments: ["shipments"] as const,
  shipment: (id: number | string) => ["shipments", String(id)] as const,
  shipmentCounts: ["shipments", "counts"] as const,
  printers: ["printers"] as const,
  palletTypes: ["pallet-types"] as const,
  labels: ["labels"] as const,
  paperwork: ["paperwork"] as const,
  alerts: ["alerts"] as const,
  notifications: ["notifications"] as const,
  reports: ["reports"] as const,
  scheduler: ["scheduler"] as const,
  as2: ["as2"] as const,
  apiClients: ["api-clients"] as const,
  webhooks: ["webhooks"] as const,
  feedback: ["feedback"] as const,
  billing: ["billing"] as const,
  platform: ["platform"] as const,
  onboarding: ["onboarding"] as const,
  staff: ["staff"] as const,
  enquiries: ["enquiries"] as const,
};
